import React, { Component, createRef } from 'react';
import { Helmet } from 'react-helmet-async';
import CustomLoading from '../../../util_components/custom_loading';
import { get_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import './style.css';

const metricLabels = {
  current_round_trip_time: 'RTT',
  available_incoming_bitrate: 'Incoming bitrate (kbps)',
  available_outgoing_bitrate: 'Outgoing bitrate (kbps)',
  packets_lost: 'Packets lost (cumulative)',
  jitter: 'Jitter',
  frames_per_second: 'FPS',
  bytes_received: 'Bytes received (cumulative)',
  bytes_sent: 'Bytes sent (cumulative)'
};

const cumulativeMetrics = new Set([
  'packets_lost',
  'frames_dropped',
  'bytes_received',
  'bytes_sent'
]);

const metricDivisors = {
  available_incoming_bitrate: 1000,
  available_outgoing_bitrate: 1000
};

const colors = {
  clientLogs: '#2166a5',
  metrics: '#2f7d54',
  line: '#d8ded7',
  muted: '#626c66'
};

const networkMetricTooltips = {
  'Bad samples':
    'Percent of stats samples where this participant crossed a network warning threshold: packet loss above 5%, RTT above 400ms, or jitter above 50ms.',
  'Inbound loss':
    'Windowed packet loss for media this participant received. This is a rate over each sampling interval, not the cumulative packets_lost counter.',
  'Uplink loss':
    'Windowed sender-side loss reported by the remote receiver for media this participant sent. Useful for spotting the participant whose upload path was poor.',
  RTT: 'Round-trip time on the selected WebRTC candidate pair. Higher values mean more network latency.',
  Jitter:
    'Variation in packet arrival timing for received media. High jitter can cause choppy audio or video even when packet loss is low.',
  'Low uplink':
    'Number of samples where available outgoing bitrate was below 400 kbps. Shown as context because browsers may report this as low when camera state or sender activity changes.'
};

class SessionAnalytics extends Component {
  timelineCanvas = createRef();

  metricCanvas = createRef();

  state = {
    loading: true,
    error: '',
    data: null,
    role: '',
    participant: '',
    metric: 'current_round_trip_time',
    search: ''
  };

  componentDidMount() {
    this.loadData();
    window.addEventListener('resize', this.renderCharts);
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.data !== this.state.data ||
      prevState.role !== this.state.role ||
      prevState.participant !== this.state.participant ||
      prevState.metric !== this.state.metric ||
      prevState.search !== this.state.search
    ) {
      this.renderCharts();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.renderCharts);
  }

  getSessionUuid = () => {
    const params = new URL(window.location.href).searchParams;
    return params.get('id') || params.get('session_uuid') || '';
  };

  loadData = () => {
    const sessionUuid = this.getSessionUuid();
    if (!sessionUuid) {
      this.setState({ loading: false, error: 'Session UUID is required.' });
      return;
    }

    this.setState({ loading: true, error: '' }, () => {
      const url = get_api_url(
        `/v2/meetings/sfu/analytics?id=${encodeURIComponent(sessionUuid)}`,
        false,
        true
      );
      get_api(url, true)
        .then((response) => {
          this.setState({ data: response.data.data, loading: false });
        })
        .catch((error) => {
          const message =
            error?.response?.data?.message ||
            error?.response?.data?.reason ||
            'Failed to load analytics.';
          this.setState({ loading: false, error: message });
        });
    });
  };

  normalizeRows = () => {
    const { data } = this.state;
    if (!data) return [];

    const clientLogs = (data.client_logs || []).map((row) => ({
      ...row,
      __table: 'client_logs',
      __ts: this.parseTime(row.timestamp),
      __participant: `${row.user_role || 'unknown'}:${row.user_uuid || ''}:${row.sid || ''}`
    }));
    const metrics = (data.metrics || []).map((row) => ({
      ...row,
      __table: 'metrics',
      __ts: this.parseTime(row.timestamp),
      __participant: `${row.user_role || 'unknown'}:${row.user_uuid || ''}:${row.sid || ''}`
    }));

    return [...clientLogs, ...metrics].sort((a, b) => a.__ts - b.__ts);
  };

  getFilteredRows = () => {
    const { role, participant, search } = this.state;
    const query = search.trim().toLowerCase();

    return this.normalizeRows().filter((row) => {
      if (role && row.user_role !== role) return false;
      if (participant && row.__participant !== participant) return false;
      if (query) {
        const haystack = `${row.event || ''} ${row.message || ''} ${row.connection_state || ''} ${
          row.ice_connection_state || ''
        }`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
  };

  parseTime = (value) => {
    if (!value) return null;
    if (value.includes('T')) {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    const parsed = new Date(`${value.replace(' ', 'T')}Z`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  formatTime = (value) => {
    const date = value instanceof Date ? value : this.parseTime(value);
    if (!date || Number.isNaN(date.getTime())) return '--';
    return date.toISOString().slice(11, 19);
  };

  formatDateTime = (value) => {
    const date = value instanceof Date ? value : this.parseTime(value);
    if (!date || Number.isNaN(date.getTime())) return '--';
    return date.toISOString().replace('T', ' ').slice(0, 19);
  };

  formatUtcDateTime = (value) => {
    const date = value instanceof Date ? value : this.parseTime(value);
    if (!date || Number.isNaN(date.getTime())) return '--';
    return `${date.toISOString().replace('T', ' ').slice(0, 19)} UTC`;
  };

  formatNumber = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(Number(value));
  };

  formatDuration = (ms) => {
    if (!Number.isFinite(ms) || ms < 0) return '--';
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m ${seconds}s`;
  };

  normalizeMetricValue = (metric, value) => {
    if (value === null || value === undefined) return null;
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return null;
    return numericValue / (metricDivisors[metric] || 1);
  };

  parsePayload = (row) => {
    if (!row?.payload) return {};
    if (typeof row.payload === 'object') return row.payload;
    try {
      return JSON.parse(row.payload);
    } catch (e) {
      return {};
    }
  };

  numericOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  };

  shortId = (value) => {
    if (!value) return '--';
    return value.length > 10 ? `${value.slice(0, 8)}...${value.slice(-4)}` : value;
  };

  getRoles = (rows) => Array.from(new Set(rows.map((row) => row.user_role).filter(Boolean))).sort();

  getParticipants = (rows) =>
    Array.from(
      new Map(
        rows.map((row) => [
          row.__participant,
          {
            value: row.__participant,
            label: `${row.user_role || 'unknown'} - ${this.shortId(row.user_uuid)} - ${this.shortId(
              row.sid
            )}`
          }
        ])
      ).values()
    ).sort((a, b) => a.label.localeCompare(b.label));

  getSummaryStats = () => {
    const { data } = this.state;
    const tables = data?.summary || [];
    const totalRows = tables.reduce((sum, table) => sum + Number(table.rows || 0), 0);
    const times = tables
      .flatMap((table) => [
        this.parseTime(table.min_timestamp),
        this.parseTime(table.max_timestamp)
      ])
      .filter(Boolean)
      .map((date) => date.getTime());
    const minTime = times.length ? Math.min(...times) : null;
    const maxTime = times.length ? Math.max(...times) : null;

    return {
      totalRows,
      duration: minTime && maxTime ? this.formatDuration(maxTime - minTime) : '--',
      users: Math.max(0, ...tables.map((table) => Number(table.users || 0))),
      devices: Math.max(0, ...tables.map((table) => Number(table.devices || 0))),
      range:
        minTime && maxTime
          ? `${this.formatDateTime(new Date(minTime))} to ${this.formatDateTime(new Date(maxTime))}`
          : '--'
    };
  };

  setupCanvas = (canvas) => {
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const cssHeight = 260;
    canvas.width = Math.max(320, Math.floor(rect.width * ratio));
    canvas.height = Math.floor(cssHeight * ratio);
    canvas.style.height = `${cssHeight}px`;
    ctx.scale(ratio, ratio);
    return { ctx, width: canvas.width / ratio, height: cssHeight };
  };

  drawAxes = (ctx, width, height, left, top, right, bottom, maxValue) => {
    ctx.clearRect(0, 0, width, height);
    ctx.strokeStyle = colors.line;
    ctx.lineWidth = 1;
    ctx.font = '12px Inter, system-ui, sans-serif';
    ctx.fillStyle = colors.muted;

    const chartHeight = height - top - bottom;
    for (let i = 0; i <= 4; i += 1) {
      const y = top + (chartHeight * i) / 4;
      ctx.beginPath();
      ctx.moveTo(left, y);
      ctx.lineTo(width - right, y);
      ctx.stroke();
      const value = maxValue - (maxValue * i) / 4;
      ctx.fillText(this.formatNumber(value), 8, y + 4);
    }
  };

  drawTimeline = (rows) => {
    const canvas = this.timelineCanvas.current;
    const setup = this.setupCanvas(canvas);
    if (!setup) return;
    const { ctx, width, height } = setup;
    const left = 52;
    const right = 12;
    const top = 16;
    const bottom = 34;
    const buckets = new Map();

    rows.forEach((row) => {
      if (!row.__ts) return;
      const minute = Math.floor(row.__ts.getTime() / 60000) * 60000;
      const bucket = buckets.get(minute) || { t: new Date(minute), client_logs: 0, metrics: 0 };
      bucket[row.__table] += 1;
      buckets.set(minute, bucket);
    });

    const data = Array.from(buckets.values()).sort((a, b) => a.t - b.t);
    const maxValue = Math.max(1, ...data.map((item) => item.client_logs + item.metrics));
    this.drawAxes(ctx, width, height, left, top, right, bottom, maxValue);

    const chartWidth = width - left - right;
    const chartHeight = height - top - bottom;
    const barWidth = Math.max(2, chartWidth / Math.max(1, data.length) - 2);
    data.forEach((item, index) => {
      const x = left + (index * chartWidth) / Math.max(1, data.length);
      let y = height - bottom;
      [
        ['client_logs', colors.clientLogs],
        ['metrics', colors.metrics]
      ].forEach(([key, color]) => {
        const barHeight = (item[key] / maxValue) * chartHeight;
        ctx.fillStyle = color;
        ctx.fillRect(x, y - barHeight, barWidth, barHeight);
        y -= barHeight;
      });
    });

    ctx.fillStyle = colors.muted;
    if (data.length) {
      ctx.fillText(this.formatTime(data[0].t), left, height - 10);
      const lastLabel = this.formatTime(data[data.length - 1].t);
      ctx.fillText(
        lastLabel,
        Math.max(left, width - right - ctx.measureText(lastLabel).width),
        height - 10
      );
    }
  };

  drawMetric = (rows) => {
    const canvas = this.metricCanvas.current;
    const setup = this.setupCanvas(canvas);
    if (!setup) return;
    const { ctx, width, height } = setup;
    const left = 58;
    const right = 14;
    const top = 16;
    const bottom = 34;
    const points = rows
      .filter(
        (row) =>
          row.__table === 'metrics' &&
          row[this.state.metric] !== null &&
          row[this.state.metric] !== undefined
      )
      .map((row) => ({
        t: row.__ts,
        value: this.normalizeMetricValue(this.state.metric, row[this.state.metric])
      }))
      .filter((point) => point.t && Number.isFinite(point.value))
      .sort((a, b) => a.t - b.t);

    if (!points.length) {
      this.drawAxes(ctx, width, height, left, top, right, bottom, 1);
      ctx.fillStyle = colors.muted;
      ctx.fillText('No samples', left, height / 2);
      return;
    }

    const values = points.map((point) => point.value);
    const maxValue = Math.max(...values);
    const minValue = Math.min(...values);
    const yMax = maxValue === minValue ? maxValue + 1 : maxValue;
    const yMin = maxValue === minValue ? Math.max(0, minValue - 1) : Math.min(0, minValue);
    const chartWidth = width - left - right;
    const chartHeight = height - top - bottom;
    const minTime = points[0].t.getTime();
    const maxTime = points[points.length - 1].t.getTime();
    const timeSpan = Math.max(1, maxTime - minTime);
    const valueSpan = Math.max(1, yMax - yMin);

    this.drawAxes(ctx, width, height, left, top, right, bottom, yMax);
    const toX = (point) => left + ((point.t.getTime() - minTime) / timeSpan) * chartWidth;
    const toY = (point) => top + (1 - (point.value - yMin) / valueSpan) * chartHeight;

    ctx.lineWidth = 2;
    ctx.strokeStyle = colors.metrics;
    ctx.beginPath();
    points.forEach((point, index) => {
      if (index === 0) ctx.moveTo(toX(point), toY(point));
      else ctx.lineTo(toX(point), toY(point));
    });
    ctx.stroke();

    ctx.fillStyle = colors.metrics;
    const stride = Math.max(1, Math.floor(points.length / 80));
    points
      .filter((_, index) => index % stride === 0)
      .forEach((point) => {
        ctx.beginPath();
        ctx.arc(toX(point), toY(point), 2.5, 0, Math.PI * 2);
        ctx.fill();
      });

    ctx.fillStyle = colors.muted;
    ctx.fillText(this.formatTime(points[0].t), left, height - 10);
    const lastLabel = this.formatTime(points[points.length - 1].t);
    ctx.fillText(
      lastLabel,
      Math.max(left, width - right - ctx.measureText(lastLabel).width),
      height - 10
    );
  };

  renderCharts = () => {
    const rows = this.getFilteredRows();
    requestAnimationFrame(() => {
      this.drawTimeline(rows);
      this.drawMetric(rows);
    });
  };

  metricStats = (rows) => {
    const values = rows
      .filter((row) => row.__table === 'metrics' && row[this.state.metric] !== null)
      .map((row) => this.normalizeMetricValue(this.state.metric, row[this.state.metric]))
      .filter((value) => Number.isFinite(value));
    if (!values.length) return 'no samples';
    const min = Math.min(...values);
    const max = Math.max(...values);
    const avg = values.reduce((sum, value) => sum + value, 0) / values.length;
    const prefix = cumulativeMetrics.has(this.state.metric) ? 'cumulative over time - ' : '';
    return `${prefix}min ${this.formatNumber(min)} - avg ${this.formatNumber(avg)} - max ${this.formatNumber(max)}`;
  };

  countBy = (rows, field) => {
    const counts = new Map();
    rows.forEach((row) => {
      const value = row[field];
      if (value) counts.set(value, (counts.get(value) || 0) + 1);
    });
    return counts;
  };

  renderTopEvents = (rows) => {
    const counts = this.countBy(rows, 'event');
    const top = Array.from(counts, ([event, count]) => ({ event, count }))
      .sort((a, b) => b.count - a.count || a.event.localeCompare(b.event))
      .slice(0, 14);
    const max = Math.max(1, ...top.map((item) => item.count));

    return (
      <div className="sfu-bar-list">
        {top.map((item) => (
          <div className="sfu-bar-row" key={item.event}>
            <div className="sfu-bar-meta">
              <span className="sfu-bar-label" title={item.event}>
                {item.event}
              </span>
              <strong>{this.formatNumber(item.count)}</strong>
            </div>
            <div className="sfu-bar-track">
              <div className="sfu-bar-fill" style={{ width: `${(item.count / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    );
  };

  renderParticipants = (rows) => {
    const participants = new Map();
    rows.forEach((row) => {
      const item = participants.get(row.__participant) || {
        role: row.user_role || 'unknown',
        user_uuid: row.user_uuid || '',
        sid: row.sid || '',
        rows: 0,
        logs: 0,
        metrics: 0,
        first: row.__ts,
        last: row.__ts
      };
      item.rows += 1;
      item.logs += row.__table === 'client_logs' ? 1 : 0;
      item.metrics += row.__table === 'metrics' ? 1 : 0;
      if (row.__ts && (!item.first || row.__ts < item.first)) item.first = row.__ts;
      if (row.__ts && (!item.last || row.__ts > item.last)) item.last = row.__ts;
      participants.set(row.__participant, item);
    });

    return Array.from(participants.values())
      .sort((a, b) => b.rows - a.rows)
      .map((item) => {
        const profileUrl =
          item.role === 'teacher'
            ? `/teacherprofile?id=${encodeURIComponent(item.user_uuid)}`
            : item.role === 'student'
              ? `/studentprofile?id=${encodeURIComponent(item.user_uuid)}`
              : '';
        const participantContent = (
          <>
            <div className={`sfu-avatar ${item.role}`}>{item.role.slice(0, 1).toUpperCase()}</div>
            <div className="sfu-participant-main">
              <div className="sfu-participant-name">
                <span>{item.role}</span>
                <span className="sfu-badge">{this.formatNumber(item.rows)} rows</span>
              </div>
              <div className="sfu-uuid">
                {this.shortId(item.user_uuid)} - {this.shortId(item.sid)}
              </div>
              <div className="sfu-muted">
                {this.formatTime(item.first)} to {this.formatTime(item.last)}
              </div>
            </div>
            <div className="sfu-muted">
              {this.formatNumber(item.logs)} logs
              <br />
              {this.formatNumber(item.metrics)} metrics
            </div>
          </>
        );

        if (!profileUrl) {
          return (
            <div className="sfu-participant" key={`${item.user_uuid}:${item.sid}`}>
              {participantContent}
            </div>
          );
        }

        return (
          <a
            className="sfu-participant sfu-participant-link"
            href={profileUrl}
            key={`${item.user_uuid}:${item.sid}`}
            target="_blank"
            rel="noreferrer"
          >
            {participantContent}
          </a>
        );
      });
  };

  summarizeValues = (values) => {
    const cleanValues = values.filter((value) => Number.isFinite(value));
    if (!cleanValues.length) return { avg: null, max: null };
    return {
      avg: cleanValues.reduce((sum, value) => sum + value, 0) / cleanValues.length,
      max: Math.max(...cleanValues)
    };
  };

  getParticipantProfileUrl = (item) => {
    if (item.role === 'teacher') return `/teacherprofile?id=${encodeURIComponent(item.user_uuid)}`;
    if (item.role === 'student') return `/studentprofile?id=${encodeURIComponent(item.user_uuid)}`;
    return '';
  };

  buildNetworkByParticipant = (rows) => {
    const participants = new Map();
    rows
      .filter((row) => row.__table === 'metrics' && row.event === 'stats_sample')
      .forEach((row) => {
        const payload = this.parsePayload(row);
        const key = row.__participant;
        const item = participants.get(key) || {
          role: row.user_role || 'unknown',
          user_uuid: row.user_uuid || '',
          sid: row.sid || '',
          samples: 0,
          badSamples: 0,
          lowOutgoingSamples: 0,
          inboundLoss: [],
          uplinkLoss: [],
          rttMs: [],
          jitterMs: [],
          outgoingKbps: [],
          qualityCounts: {}
        };

        const inboundLoss = this.numericOrNull(
          payload.inboundLossPercentWindowed ?? payload.packetLossPercent
        );
        const uplinkLoss = this.numericOrNull(
          payload.sendLossPercentWindowed ?? payload.uplinkPacketLossPercent
        );
        const rttMs = this.numericOrNull(
          payload.rttMs ??
            (row.current_round_trip_time !== null && row.current_round_trip_time !== undefined
              ? Number(row.current_round_trip_time) * 1000
              : null)
        );
        const jitterMs = this.numericOrNull(
          payload.jitterMs ??
            (row.jitter !== null && row.jitter !== undefined ? Number(row.jitter) * 1000 : null)
        );
        const outgoingKbps = this.numericOrNull(
          payload.availableOutgoingBitrateKbps ??
            (row.available_outgoing_bitrate !== null && row.available_outgoing_bitrate !== undefined
              ? Number(row.available_outgoing_bitrate) / 1000
              : null)
        );

        const badSample =
          (inboundLoss ?? 0) > 5 ||
          (uplinkLoss ?? 0) > 5 ||
          (rttMs ?? 0) > 400 ||
          (jitterMs ?? 0) > 50;

        item.samples += 1;
        item.badSamples += badSample ? 1 : 0;
        item.lowOutgoingSamples += outgoingKbps !== null && outgoingKbps < 400 ? 1 : 0;
        if (inboundLoss !== null) item.inboundLoss.push(inboundLoss);
        if (uplinkLoss !== null) item.uplinkLoss.push(uplinkLoss);
        if (rttMs !== null) item.rttMs.push(rttMs);
        if (jitterMs !== null) item.jitterMs.push(jitterMs);
        if (outgoingKbps !== null) item.outgoingKbps.push(outgoingKbps);
        if (payload.quality) {
          item.qualityCounts[payload.quality] = (item.qualityCounts[payload.quality] || 0) + 1;
        }

        participants.set(key, item);
      });

    return Array.from(participants.values())
      .map((item) => {
        const inbound = this.summarizeValues(item.inboundLoss);
        const uplink = this.summarizeValues(item.uplinkLoss);
        const rtt = this.summarizeValues(item.rttMs);
        const jitter = this.summarizeValues(item.jitterMs);
        const outgoing = this.summarizeValues(item.outgoingKbps);
        const badPercent = item.samples ? (item.badSamples / item.samples) * 100 : 0;
        const worstLoss = Math.max(inbound.max || 0, uplink.max || 0);
        const status =
          badPercent >= 15
            ? 'Needs attention'
            : badPercent >= 3 || worstLoss > 10 || (rtt.max || 0) > 800 || (jitter.max || 0) > 100
              ? 'Some issues'
              : 'Looks ok';

        return {
          ...item,
          inbound,
          uplink,
          rtt,
          jitter,
          outgoing,
          badPercent,
          status
        };
      })
      .sort((a, b) => b.badPercent - a.badPercent || b.badSamples - a.badSamples);
  };

  renderNetworkByParticipant = (rows) => {
    const participants = this.buildNetworkByParticipant(rows);
    if (!participants.length) {
      return <div className="sfu-muted">No stats samples available.</div>;
    }

    return (
      <div className="sfu-network-list">
        {participants.map((item) => {
          const profileUrl = this.getParticipantProfileUrl(item);
          const statusClass =
            item.status === 'Needs attention'
              ? 'bad'
              : item.status === 'Some issues'
                ? 'warn'
                : 'ok';
          return (
            <div className="sfu-network-card" key={`${item.user_uuid}:${item.sid}`}>
              <div className="sfu-network-card-head">
                <div>
                  <a
                    className="sfu-network-person"
                    href={profileUrl || undefined}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {item.role} - {this.shortId(item.user_uuid)}
                  </a>
                  <div className="sfu-muted">{this.shortId(item.sid)}</div>
                </div>
                <span className={`sfu-health ${statusClass}`}>{item.status}</span>
              </div>
              <div className="sfu-network-metrics">
                <div>
                  {this.renderNetworkMetricLabel('Bad samples')}
                  <strong>
                    {this.formatNumber(item.badPercent)}% ({this.formatNumber(item.badSamples)}/
                    {this.formatNumber(item.samples)})
                  </strong>
                </div>
                <div>
                  {this.renderNetworkMetricLabel('Inbound loss')}
                  <strong>
                    avg {this.formatNumber(item.inbound.avg)}% / max{' '}
                    {this.formatNumber(item.inbound.max)}%
                  </strong>
                </div>
                <div>
                  {this.renderNetworkMetricLabel('Uplink loss')}
                  <strong>
                    avg {this.formatNumber(item.uplink.avg)}% / max{' '}
                    {this.formatNumber(item.uplink.max)}%
                  </strong>
                </div>
                <div>
                  {this.renderNetworkMetricLabel('RTT')}
                  <strong>
                    avg {this.formatNumber(item.rtt.avg)}ms / max {this.formatNumber(item.rtt.max)}
                    ms
                  </strong>
                </div>
                <div>
                  {this.renderNetworkMetricLabel('Jitter')}
                  <strong>
                    avg {this.formatNumber(item.jitter.avg)}ms / max{' '}
                    {this.formatNumber(item.jitter.max)}ms
                  </strong>
                </div>
                <div>
                  {this.renderNetworkMetricLabel('Low uplink')}
                  <strong>
                    {this.formatNumber(item.lowOutgoingSamples)} samples under 400 kbps
                  </strong>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  renderNetworkMetricLabel = (label) => (
    <span
      className="sfu-help-label"
      data-tooltip-id="my-tooltip"
      data-tooltip-content={networkMetricTooltips[label]}
    >
      {label}
    </span>
  );

  renderStateSummary = (rows) => {
    const fields = [
      'connection_state',
      'ice_connection_state',
      'ice_gathering_state',
      'signaling_state'
    ];
    return fields.map((field) => {
      const counts = this.countBy(rows, field);
      const top = Array.from(counts, ([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 4);
      return (
        <div className="sfu-chip" key={field}>
          <span>{field.replaceAll('_', ' ')}</span>
          <strong>{this.formatNumber(counts.size)}</strong>
          <span>
            {top.map((item) => `${item.value} ${this.formatNumber(item.count)}`).join(' - ') ||
              '--'}
          </span>
        </div>
      );
    });
  };

  isDisconnectSignal = (row) => {
    const event = row.event || '';
    const stateText =
      `${row.connection_state || ''} ${row.ice_connection_state || ''}`.toLowerCase();
    return (
      row.disconnect_reason ||
      /disconnected|failed|closed/.test(stateText) ||
      /transport:recovery:scheduled/i.test(event)
    );
  };

  isRecoveryAction = (row) =>
    /transport:ice-restart|transport:session-recreate|transport:browser-recovery-trigger|transport:reconnected|transport:recovered/i.test(
      row.event || ''
    );

  isReconnectSignal = (row, episode) => {
    const event = row.event || '';
    const connected =
      row.connection_state === 'connected' && row.ice_connection_state === 'connected';
    if (/transport:recovered|transport:session-recreate:success/i.test(event)) return true;
    if (episode?.events?.some((item) => item.event === 'transport:session-recreate:start')) {
      return false;
    }
    return /transport:reconnected/i.test(event) || connected;
  };

  inferReconnectCause = (episode) => {
    const events = new Set(episode.events.map((item) => item.event));
    const reasons = episode.events.map((item) => item.disconnect_reason).filter(Boolean);
    if (events.has('transport:ice-restart:failed'))
      return 'ICE restart failed; SFU session recreated';
    if (events.has('transport:session-recreate:start')) {
      return 'SFU session recreated after transport disconnect';
    }
    if (events.has('transport:browser-recovery-trigger')) return 'Browser recovery triggered';
    if (events.has('transport:recovery:scheduled')) return 'WebRTC transport disconnected';
    if (reasons.length) return reasons[0].replaceAll('_', ' ');
    if (episode.events.some((item) => item.ice_connection_state === 'disconnected')) {
      return 'ICE connection disconnected';
    }
    return 'Connection state changed';
  };

  summarizeReconnects = (rows) => {
    const byParticipant = new Map();
    rows
      .filter((row) => row.__table === 'client_logs' || row.__table === 'metrics')
      .filter((row) => row.__ts)
      .sort((a, b) => a.__ts - b.__ts)
      .forEach((row) => {
        const key = row.__participant;
        const item = byParticipant.get(key) || {
          role: row.user_role || 'unknown',
          user_uuid: row.user_uuid || '',
          sid: row.sid || '',
          activeEpisode: null,
          episodes: [],
          browserRecoveryTriggers: []
        };

        const disconnectSignal = this.isDisconnectSignal(row);
        const recoveryAction = this.isRecoveryAction(row);

        if (row.event === 'transport:browser-recovery-trigger' && !item.activeEpisode) {
          item.browserRecoveryTriggers.push(row);
        }

        if (disconnectSignal && !item.activeEpisode) {
          item.activeEpisode = {
            start: row.__ts,
            end: null,
            role: item.role,
            user_uuid: item.user_uuid,
            sid: item.sid,
            events: []
          };
        }

        if (item.activeEpisode && (disconnectSignal || recoveryAction)) {
          item.activeEpisode.events.push(row);
        }

        if (item.activeEpisode && this.isReconnectSignal(row, item.activeEpisode)) {
          item.activeEpisode.end = row.__ts;
          item.activeEpisode.cause = this.inferReconnectCause(item.activeEpisode);
          item.episodes.push(item.activeEpisode);
          item.activeEpisode = null;
        }

        byParticipant.set(key, item);
      });

    Array.from(byParticipant.values()).forEach((item) => {
      if (item.activeEpisode) {
        item.activeEpisode.cause = this.inferReconnectCause(item.activeEpisode);
        item.episodes.push(item.activeEpisode);
        item.activeEpisode = null;
      }
    });

    return Array.from(byParticipant.values())
      .map((item) => {
        const causeCounts = new Map();
        item.episodes.forEach((episode) => {
          causeCounts.set(episode.cause, (causeCounts.get(episode.cause) || 0) + 1);
        });
        const primaryCause =
          Array.from(causeCounts, ([cause, count]) => ({ cause, count })).sort(
            (a, b) => b.count - a.count
          )[0]?.cause || '--';
        const reconnects = item.episodes.filter((episode) => episode.end).length;
        return { ...item, primaryCause, reconnects };
      })
      .filter((item) => item.episodes.length || item.browserRecoveryTriggers.length)
      .sort(
        (a, b) =>
          b.episodes.length - a.episodes.length ||
          b.browserRecoveryTriggers.length - a.browserRecoveryTriggers.length ||
          a.role.localeCompare(b.role)
      );
  };

  cumulativeMaxByParticipant = (rows, field) => {
    const byParticipant = new Map();
    rows.forEach((row) => {
      const value = Number(row[field]);
      if (!Number.isFinite(value)) return;
      const current = byParticipant.get(row.__participant) || 0;
      byParticipant.set(row.__participant, Math.max(current, value));
    });
    return Array.from(byParticipant.values()).reduce((total, value) => total + value, 0);
  };

  renderQualitySignals = (rows) => {
    const metrics = rows.filter((row) => row.__table === 'metrics');
    const reconnectParticipants = this.summarizeReconnects(rows);
    const disconnectEpisodes = reconnectParticipants.reduce(
      (total, item) => total + item.episodes.length,
      0
    );
    const signals = [
      [
        'Packets lost (cumulative)',
        this.cumulativeMaxByParticipant(metrics, 'packets_lost'),
        "sum of each participant's max counter; use loss % for severity"
      ],
      [
        'Frames dropped (cumulative)',
        this.cumulativeMaxByParticipant(metrics, 'frames_dropped'),
        "sum of each participant's max counter; not summed per sample"
      ],
      ['Disconnect episodes', disconnectEpisodes, 'grouped to match Reconnect Analysis'],
      [
        'ICE failed rows',
        metrics.filter((row) => row.ice_connection_state === 'failed').length,
        'raw metric rows where ICE state was failed'
      ],
      [
        'Relay rows',
        rows.filter((row) => /relay/i.test(row.event || '')).length,
        'raw rows mentioning relay activity'
      ],
      [
        'Negotiation rows',
        rows.filter((row) => /renegotiate|signaling/i.test(row.event || '')).length,
        'raw rows mentioning signaling or renegotiation'
      ]
    ];

    return signals.map(([label, value, note]) => (
      <div className="sfu-signal" key={label}>
        <span>{label}</span>
        <strong>{this.formatNumber(value)}</strong>
        <span className="sfu-signal-note">{note}</span>
      </div>
    ));
  };

  renderReconnectAnalysis = (rows) => {
    const participants = this.summarizeReconnects(rows);
    const episodes = participants
      .flatMap((item) =>
        item.episodes.map((episode) => ({
          ...episode,
          role: item.role,
          user_uuid: item.user_uuid,
          sid: item.sid
        }))
      )
      .sort((a, b) => a.start - b.start);

    return (
      <>
        <div className="sfu-reconnect-grid">
          {participants.length ? (
            participants.map((item) => (
              <div className="sfu-reconnect-card" key={`${item.user_uuid}:${item.sid}`}>
                <div>
                  <strong>
                    {item.role} - {this.shortId(item.user_uuid)}
                  </strong>
                  <div className="sfu-muted">{this.shortId(item.sid)}</div>
                </div>
                <div className="sfu-reconnect-stats">
                  <span>
                    Disconnected <strong>{this.formatNumber(item.episodes.length)}</strong>
                  </span>
                  <span>
                    Reconnected <strong>{this.formatNumber(item.reconnects)}</strong>
                  </span>
                  <span>
                    Browser triggers{' '}
                    <strong>{this.formatNumber(item.browserRecoveryTriggers.length)}</strong>
                  </span>
                </div>
                <div className="sfu-muted">Likely cause: {item.primaryCause}</div>
              </div>
            ))
          ) : (
            <div className="sfu-muted">
              No disconnect or reconnect signals in the current filter.
            </div>
          )}
        </div>

        {episodes.length ? (
          <div className="sfu-reconnect-list">
            {episodes.map((episode, index) => {
              const flow = Array.from(
                new Set(episode.events.map((row) => row.event).filter(Boolean))
              ).slice(0, 6);
              const duration = episode.end
                ? this.formatDuration(episode.end - episode.start)
                : 'open';
              return (
                <div
                  className="sfu-reconnect-event"
                  key={`${episode.user_uuid}:${episode.start?.getTime() || index}`}
                >
                  <div>
                    <span className="sfu-event-time-label">Start UTC</span>
                    <strong>{this.formatUtcDateTime(episode.start)}</strong>
                    <div className="sfu-muted">
                      {episode.role} - {this.shortId(episode.user_uuid)} -{' '}
                      {this.shortId(episode.sid)}
                    </div>
                  </div>
                  <div>
                    <span className="sfu-badge">{duration}</span>
                  </div>
                  <div>
                    <strong>{episode.cause}</strong>
                    <div className="sfu-muted">{flow.join(' -> ')}</div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </>
    );
  };

  getSampleDuration = (rows, index) => {
    const current = rows[index]?.__ts;
    const next = rows[index + 1]?.__ts;
    if (!current || !next) return 3000;
    return Math.min(10000, Math.max(1000, next.getTime() - current.getTime()));
  };

  getMediaStatus = (row) => {
    const payload = this.parsePayload(row);
    const fps = this.numericOrNull(row.frames_per_second);
    const inboundLoss = this.numericOrNull(
      payload.inboundLossPercentWindowed ?? payload.packetLossPercent
    );
    const uplinkLoss = this.numericOrNull(
      payload.sendLossPercentWindowed ?? payload.uplinkPacketLossPercent
    );
    const rttMs = this.numericOrNull(
      payload.rttMs ??
        (row.current_round_trip_time !== null && row.current_round_trip_time !== undefined
          ? Number(row.current_round_trip_time) * 1000
          : null)
    );
    const jitterMs = this.numericOrNull(
      payload.jitterMs ??
        (row.jitter !== null && row.jitter !== undefined ? Number(row.jitter) * 1000 : null)
    );
    const quality = String(payload.quality || '').toLowerCase();
    const frozen = fps !== null && fps <= 1;
    const lagReasons = [];

    if (!frozen && fps !== null && fps < 10) lagReasons.push('low video FPS');
    if (!frozen && (quality === 'poor' || quality === 'lost'))
      lagReasons.push(`quality ${quality}`);
    if (inboundLoss !== null && inboundLoss > 5) lagReasons.push('inbound packet loss');
    if (uplinkLoss !== null && uplinkLoss > 5) lagReasons.push('uplink packet loss');
    if (rttMs !== null && rttMs > 400) lagReasons.push('high RTT');
    if (jitterMs !== null && jitterMs > 50) lagReasons.push('high jitter');
    if (row.connection_state === 'disconnected' || row.ice_connection_state === 'disconnected') {
      lagReasons.push('transport disconnected');
    }

    return {
      frozen,
      laggy: !frozen && lagReasons.length > 0,
      reasons: frozen ? ['video FPS at or below 1'] : lagReasons,
      fps,
      inboundLoss,
      uplinkLoss,
      rttMs,
      jitterMs,
      quality
    };
  };

  addMediaEpisode = (episodes, row, duration, status) => {
    const end = new Date(row.__ts.getTime() + duration);
    const last = episodes[episodes.length - 1];
    if (last && row.__ts.getTime() - last.end.getTime() <= 10000) {
      last.end = end;
      last.durationMs += duration;
      last.samples += 1;
      status.reasons.forEach((reason) => {
        last.reasonCounts[reason] = (last.reasonCounts[reason] || 0) + 1;
      });
      return;
    }

    const reasonCounts = {};
    status.reasons.forEach((reason) => {
      reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
    });
    episodes.push({
      start: row.__ts,
      end,
      durationMs: duration,
      samples: 1,
      reasonCounts
    });
  };

  summarizeMediaExperience = (rows) => {
    const byParticipant = new Map();
    rows
      .filter((row) => row.__table === 'metrics' && row.event === 'stats_sample' && row.__ts)
      .sort((a, b) => a.__ts - b.__ts)
      .forEach((row) => {
        const item = byParticipant.get(row.__participant) || {
          role: row.user_role || 'unknown',
          user_uuid: row.user_uuid || '',
          sid: row.sid || '',
          samples: [],
          frozenMs: 0,
          laggyMs: 0,
          frozenSamples: 0,
          laggySamples: 0,
          frozenEpisodes: [],
          laggyEpisodes: [],
          reasonCounts: {}
        };
        item.samples.push(row);
        byParticipant.set(row.__participant, item);
      });

    return Array.from(byParticipant.values())
      .map((item) => {
        item.samples.forEach((row, index) => {
          const duration = this.getSampleDuration(item.samples, index);
          const status = this.getMediaStatus(row);
          if (status.frozen) {
            item.frozenMs += duration;
            item.frozenSamples += 1;
            this.addMediaEpisode(item.frozenEpisodes, row, duration, status);
          } else if (status.laggy) {
            item.laggyMs += duration;
            item.laggySamples += 1;
            this.addMediaEpisode(item.laggyEpisodes, row, duration, status);
          }
          status.reasons.forEach((reason) => {
            item.reasonCounts[reason] = (item.reasonCounts[reason] || 0) + 1;
          });
        });

        const totalMs = item.samples.reduce(
          (total, _row, index) => total + this.getSampleDuration(item.samples, index),
          0
        );
        const affectedMs = item.frozenMs + item.laggyMs;
        const topReasons = Object.entries(item.reasonCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([reason]) => reason);

        return {
          ...item,
          totalMs,
          affectedMs,
          affectedPercent: totalMs ? (affectedMs / totalMs) * 100 : 0,
          topReasons
        };
      })
      .filter((item) => item.samples.length)
      .sort((a, b) => b.affectedMs - a.affectedMs || b.frozenMs - a.frozenMs);
  };

  renderMediaExperience = (rows) => {
    const participants = this.summarizeMediaExperience(rows);
    if (!participants.length) {
      return <div className="sfu-muted">No stats samples available.</div>;
    }

    const episodes = participants
      .flatMap((item) => [
        ...item.frozenEpisodes.map((episode) => ({
          ...episode,
          type: 'Frozen',
          participant: item
        })),
        ...item.laggyEpisodes.map((episode) => ({ ...episode, type: 'Laggy', participant: item }))
      ])
      .sort((a, b) => a.start - b.start)
      .slice(0, 8);

    return (
      <>
        <div className="sfu-media-grid">
          {participants.map((item) => (
            <div className="sfu-media-card" key={`${item.user_uuid}:${item.sid}`}>
              <div className="sfu-media-card-head">
                <div>
                  <strong>
                    {item.role} - {this.shortId(item.user_uuid)}
                  </strong>
                  <div className="sfu-muted">{this.shortId(item.sid)}</div>
                </div>
                <span className="sfu-badge">
                  {this.formatNumber(item.affectedPercent)}% affected
                </span>
              </div>
              <div className="sfu-media-stats">
                <span>
                  Frozen <strong>{this.formatDuration(item.frozenMs)}</strong>
                </span>
                <span>
                  Laggy <strong>{this.formatDuration(item.laggyMs)}</strong>
                </span>
                <span>
                  Episodes{' '}
                  <strong>
                    {this.formatNumber(item.frozenEpisodes.length + item.laggyEpisodes.length)}
                  </strong>
                </span>
              </div>
              <div className="sfu-muted">
                Likely drivers: {item.topReasons.length ? item.topReasons.join(', ') : 'none'}
              </div>
            </div>
          ))}
        </div>

        {episodes.length ? (
          <div className="sfu-media-list">
            {episodes.map((episode, index) => {
              const reasons = Object.entries(episode.reasonCounts)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 3)
                .map(([reason]) => reason)
                .join(', ');
              return (
                <div
                  className="sfu-media-event"
                  key={`${episode.type}:${episode.start?.getTime() || index}:${episode.participant.user_uuid}`}
                >
                  <div>
                    <span className="sfu-event-time-label">Start UTC</span>
                    <strong>{this.formatUtcDateTime(episode.start)}</strong>
                    <div className="sfu-muted">
                      {episode.type} - {episode.participant.role} -{' '}
                      {this.shortId(episode.participant.user_uuid)}
                    </div>
                  </div>
                  <div>
                    <span className="sfu-badge">{this.formatDuration(episode.durationMs)}</span>
                  </div>
                  <div>
                    <strong>{reasons || '--'}</strong>
                    <div className="sfu-muted">{this.formatNumber(episode.samples)} samples</div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </>
    );
  };

  renderRows = (rows) =>
    rows
      .slice(-300)
      .reverse()
      .map((row, index) => (
        <tr key={`${row.__table}:${row.timestamp}:${row.event}:${index}`}>
          <td>{this.formatTime(row.__ts)}</td>
          <td>{row.__table === 'metrics' ? 'metrics' : 'client logs'}</td>
          <td>{row.user_role}</td>
          <td>{row.event}</td>
          <td>{row.connection_state || row.ice_connection_state}</td>
          <td>{row.message || row.disconnect_reason}</td>
        </tr>
      ));

  render() {
    const { loading, error, data, role, participant, metric, search } = this.state;
    const allRows = this.normalizeRows();
    const rows = this.getFilteredRows();
    const roles = this.getRoles(allRows);
    const participants = this.getParticipants(allRows);
    const stats = this.getSummaryStats();

    if (loading) {
      return <CustomLoading />;
    }

    if (error) {
      return (
        <div className="sfu-page">
          <Helmet>
            <title>Session Analytics</title>
          </Helmet>
          <div className="sfu-error">{error}</div>
        </div>
      );
    }

    return (
      <div className="sfu-page">
        <Helmet>
          <title>Session Analytics</title>
        </Helmet>

        <header className="sfu-topbar">
          <div>
            <p className="sfu-eyebrow">SFU Session</p>
            <h1>{this.shortId(data.session_uuid)}</h1>
            <p className="sfu-muted">{stats.range}</p>
          </div>
          <button
            type="button"
            className="sfu-icon-button"
            onClick={this.loadData}
            title="Reload data"
          >
            ↻
          </button>
        </header>

        <main className="sfu-main">
          {data.truncated && (
            <div className="sfu-warning">
              This session has more than {this.formatNumber(data.row_limit)} rows in at least one
              source table, so raw rows are capped for browser performance.
            </div>
          )}

          <section className="sfu-status-band">
            <div className="sfu-metric-tile">
              <span>Total Rows</span>
              <strong>{this.formatNumber(stats.totalRows)}</strong>
            </div>
            <div className="sfu-metric-tile">
              <span>Duration</span>
              <strong>{stats.duration}</strong>
            </div>
            <div className="sfu-metric-tile">
              <span>Users</span>
              <strong>{this.formatNumber(stats.users)}</strong>
            </div>
            <div className="sfu-metric-tile">
              <span>Devices</span>
              <strong>{this.formatNumber(stats.devices)}</strong>
            </div>
            <div className="sfu-metric-tile">
              <span>Source DB</span>
              <strong>{data.source_database || '--'}</strong>
            </div>
          </section>

          <section className="sfu-toolbar">
            <label>
              <span>Role</span>
              <select value={role} onChange={(e) => this.setState({ role: e.target.value })}>
                <option value="">All roles</option>
                {roles.map((item) => (
                  <option value={item} key={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Participant</span>
              <select
                value={participant}
                onChange={(e) => this.setState({ participant: e.target.value })}
              >
                <option value="">All participants</option>
                {participants.map((item) => (
                  <option value={item.value} key={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Metric</span>
              <select value={metric} onChange={(e) => this.setState({ metric: e.target.value })}>
                {Object.keys(metricLabels).map((key) => (
                  <option value={key} key={key}>
                    {metricLabels[key]}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Event Search</span>
              <input
                type="search"
                value={search}
                onChange={(e) => this.setState({ search: e.target.value })}
                placeholder="event or message"
              />
            </label>
          </section>

          <section className="sfu-grid two">
            <article className="sfu-panel">
              <div className="sfu-panel-head">
                <div>
                  <p className="sfu-eyebrow">Timeline</p>
                  <h2>Rows Per Minute</h2>
                </div>
                <span className="sfu-muted">{this.formatNumber(rows.length)} shown</span>
              </div>
              <canvas ref={this.timelineCanvas} height="260" />
            </article>
            <article className="sfu-panel">
              <div className="sfu-panel-head">
                <div>
                  <p className="sfu-eyebrow">Network</p>
                  <h2>{metricLabels[metric]}</h2>
                </div>
                <span className="sfu-muted">{this.metricStats(rows)}</span>
              </div>
              <canvas ref={this.metricCanvas} height="260" />
            </article>
          </section>

          <section className="sfu-panel">
            <div className="sfu-panel-head">
              <div>
                <p className="sfu-eyebrow">Network</p>
                <h2>Network By Participant</h2>
              </div>
              <span className="sfu-muted">
                Uses windowed packet loss, RTT, jitter, and outgoing bitrate
              </span>
            </div>
            {this.renderNetworkByParticipant(rows)}
          </section>

          <section className="sfu-panel">
            <div className="sfu-panel-head">
              <div>
                <p className="sfu-eyebrow">Media</p>
                <h2>Media Experience</h2>
              </div>
              <span className="sfu-muted">
                Frozen = FPS at or below 1; laggy = poor quality, loss, RTT, jitter, or low FPS
              </span>
            </div>
            {this.renderMediaExperience(rows)}
          </section>

          <section className="sfu-grid two">
            <article className="sfu-panel">
              <div className="sfu-panel-head">
                <div>
                  <p className="sfu-eyebrow">Events</p>
                  <h2>Top Events</h2>
                </div>
              </div>
              {this.renderTopEvents(rows)}
            </article>
            <article className="sfu-panel">
              <div className="sfu-panel-head">
                <div>
                  <p className="sfu-eyebrow">Participants</p>
                  <h2>Activity By Role</h2>
                </div>
              </div>
              <div className="sfu-participant-list">{this.renderParticipants(rows)}</div>
            </article>
          </section>

          <section className="sfu-grid two">
            <article className="sfu-panel">
              <div className="sfu-panel-head">
                <div>
                  <p className="sfu-eyebrow">Connection</p>
                  <h2>State Changes</h2>
                </div>
              </div>
              <div className="sfu-chip-grid">{this.renderStateSummary(rows)}</div>
            </article>
            <article className="sfu-panel">
              <div className="sfu-panel-head">
                <div>
                  <p className="sfu-eyebrow">Quality</p>
                  <h2>Signals</h2>
                </div>
              </div>
              <div className="sfu-signal-grid">{this.renderQualitySignals(rows)}</div>
            </article>
          </section>

          <section className="sfu-panel">
            <div className="sfu-panel-head">
              <div>
                <p className="sfu-eyebrow">Connection</p>
                <h2>Reconnect Analysis</h2>
              </div>
              <span className="sfu-muted">
                Groups nearby disconnect and recovery logs into episodes
              </span>
            </div>
            {this.renderReconnectAnalysis(rows)}
          </section>

          <section className="sfu-panel">
            <div className="sfu-panel-head">
              <div>
                <p className="sfu-eyebrow">Rows</p>
                <h2>Event Stream</h2>
              </div>
              <span className="sfu-muted">latest 300</span>
            </div>
            <div className="sfu-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Table</th>
                    <th>Role</th>
                    <th>Event</th>
                    <th>State</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>{this.renderRows(rows)}</tbody>
              </table>
            </div>
          </section>
        </main>
      </div>
    );
  }
}

export default SessionAnalytics;
